<template>
  <main>
    <AnalyticsControlsBar
      class="display-wrapper"
      :spin="reloadButtonNeedsToSpin"
      :range="currentRange"
      :process-group-filter="processGroupFilter"
      :queue-group-filter="queueGroupFilter"
      @updateData="handleRangeChange"
      @update:processExcluded="handleProcessExcluded"
      @update:queueExcluded="handleQueueExcluded"
      @databaseChange="handleDatabaseChange"
    />

    <div class="split-row row">
      <StatsGrid class="display-wrapper" ref="overviewCards" :count="4" />

      <BaseChart
        class="box-shadow display-wrapper"
        ref="jobStatusPerMonth"
        chart-type="bar"
        title="Job Status by Month"
        :subtitle="annotateSubtitle('Monthly job outcomes', currentRange, todayIso)"
        label-y-axis="Jobs"
        :color-map="stateColorMap"
        :data-processor="transformJobStatusPerMonth"
        :category-extractor="jobStats => transformJobStatusPerMonth(jobStats).xaxisCategories"
      />
    </div>
    <div class="equal-row row">
      <BaseChart
        ref="averageQueueCompletionTime"
        chart-type="bar"
        title="Average Completion Time"
        :subtitle="annotateSubtitle('By Process', currentRange, todayIso)"
        label-y-axis="Seconds"
        :horizontal="true"
        :show-total="false"
        :data-processor="transformAverageQueueTime"
        class="box-shadow display-wrapper"
      />

      <BaseChart
        ref="processesByMonth"
        chart-type="bar"
        title="Processes Released"
        :subtitle="annotateSubtitle('New processes released monthly', currentRange, todayIso)"
        label-y-axis="Count"
        :data-processor="transformProcessesByMonthMapped"
        class="box-shadow display-wrapper"
      />
    </div>
    <div class="split-row row">
      <BaseChart
        ref="taskByStatus"
        chart-type="pie"
        title="Tasks by Status"
        :subtitle="annotateSubtitle('State distribution of tasks', currentRange, todayIso)"
        :color-map="stateColorMap"
        :data-processor="transformTasksByStatus"
        :extract-unique-items="queueStats => transformTasksByStatus(queueStats).map(item => item.name)"
        class="box-shadow display-wrapper"
      />
      <BaseChart
        ref="tasksPerMonth"
        chart-type="bar"
        title="Tasks per Month"
        :subtitle="annotateSubtitle('Monthly processed tasks', currentRange, todayIso)"
        label-y-axis="Tasks"
        :data-processor="transformTasksPerMonth"
        class="box-shadow display-wrapper"
      />
    </div>
    <div class="split-row-right row">
      <BaseChart
        ref="averageJobCompletionTime"
        chart-type="bar"
        title="Average Job Duration"
        :subtitle="annotateSubtitle('By Job', currentRange, todayIso)"
        label-y-axis="Seconds"
        :horizontal="true"
        :show-total="false"
        :data-processor="transformAvgJobRuntime"
        class="box-shadow display-wrapper"
      />

      <BaseChart
        ref="jobsByState"
        chart-type="pie"
        title="Jobs by State"
        :subtitle="annotateSubtitle('Distribution of job states', currentRange, todayIso)"
        :color-map="stateColorMap"
        :data-processor="transformJobsByState"
        :extract-unique-items="jobStats => transformJobsByState(jobStats).map(item => item.name)"
        class="box-shadow display-wrapper"
      />
    </div>
  </main>
</template>

<script setup>
import { onMounted, computed, ref, watch } from 'vue'
import { useDateRangeStore } from '@/stores/useDateRangeStore'
import { useGroupFilterStore } from '@/stores/useGroupFilterStore'
import { useDatabaseStore } from '@/stores/useDatabaseStore'
import { useGroupFilteredData, GROUP_FILTER_EMPTY_MESSAGE } from '@/Analytics/composables/useGroupFilteredData'

import AnalyticsControlsBar from '@/Analytics/components/AnalyticsControlsBar.vue'
import StatsGrid from '@/Analytics/components/StatsGrid.vue'
import BaseChart from '@/components/Graph/BaseChart.vue'

import { getProcessesMapped } from '@/Services/Ressources/API/ProcessService'
import { getJobStats, getQueueStats } from '@/Services/Ressources/API/OperationelService'
import { getAllProcessRoiStats, getAllQueueRoiStats } from '@/Services/Ressources/API/ForretningService'

import {
  transformOverviewStats,
  transformAverageQueueTime,
  transformAvgJobRuntime,
  transformJobStatusPerMonth,
  transformJobsByState,
  transformProcessesByMonthMapped,
  transformTasksByStatus,
  transformTasksPerMonth
} from '@/Modules/Grafer/dataProcessor/operationsDataConvert'
import { useRangeGraphUpdater, wrapRequestInRange } from '@/Modules/Grafer/common/useUpdateData'
import { stateColorMap } from '@/Modules/Grafer/common/stateMappings'
import { annotateSubtitle, toIsoDate } from '@/Modules/utils/dateRange'

const todayIso = toIsoDate(new Date())

const queueStatsRef = ref([])
const jobStatsRef = ref([])
const processRoiRef = ref([])
const queueRoiRef = ref([])

const databaseStore = useDatabaseStore()

const fetchData = async (range, forceUpdate) => {
  const db = databaseStore.selected
  const [queueStats, jobStats, processes, processRoiStats, queueRoiStats] = await Promise.all([
    wrapRequestInRange(getQueueStats, range.from, range.to, forceUpdate, db),
    wrapRequestInRange(getJobStats, range.from, range.to, forceUpdate, db),
    getProcessesMapped(),
    wrapRequestInRange(getAllProcessRoiStats, range.from, range.to, forceUpdate, db),
    wrapRequestInRange(getAllQueueRoiStats, range.from, range.to, forceUpdate, db)
  ])

  queueStatsRef.value = queueStats || []
  jobStatsRef.value = jobStats || []
  processRoiRef.value = processRoiStats || []
  queueRoiRef.value = queueRoiStats || []

  return {
    queueStats,
    jobStats,
    processes
  }
}

const populateMap = {
  processesByMonth: {
    method: 'populateComponent',
    getData: ({ processes }) => processes
  }
}

const dateRangeStore = useDateRangeStore()
const currentRange = computed(() => dateRangeStore.getRange('operations'))

const emptyStateOptions = (filtered, raw) =>
  filtered.length === 0 && raw.length > 0 ? { emptyMessage: GROUP_FILTER_EMPTY_MESSAGE } : {}

const groupFilterStore = useGroupFilterStore()
const processExcluded = computed(() => groupFilterStore.getExcluded('operations', 'process'))

const roiGroupMap = computed(() => {
  const map = new Map()
  for (const row of processRoiRef.value) {
    if (row.processName != null && row.groupId != null) {
      map.set(row.processName, { groupId: row.groupId, groupName: row.groupName })
    }
  }
  return map
})

const jobGroupId = (job) => roiGroupMap.value.get(job.releaseName)?.groupId ?? null
const jobGroupName = (job) => roiGroupMap.value.get(job.releaseName)?.groupName

const processHasUngrouped = computed(() => jobStatsRef.value.some((j) => jobGroupId(j) == null))

const { filteredData: filteredJobStats, groups: processGroups } = useGroupFilteredData({
  data: jobStatsRef,
  excluded: processExcluded,
  extractGroupId: jobGroupId,
  extractGroupName: jobGroupName,
  hasUngrouped: processHasUngrouped
})

const processGroupFilter = computed(() => ({
  groups: processGroups.value,
  excluded: processExcluded.value,
  label: 'Process groups',
  hasUngrouped: processHasUngrouped.value
}))

const handleProcessExcluded = (next) => {
  groupFilterStore.setExcluded('operations', 'process', next)
}

const queueExcluded = computed(() => groupFilterStore.getExcluded('operations', 'queue'))

const queueRoiGroupMap = computed(() => {
  const map = new Map()
  for (const row of queueRoiRef.value) {
    if (row.queueName != null && row.groupId != null) {
      map.set(row.queueName, { groupId: row.groupId, groupName: row.groupName })
    }
  }
  return map
})

const queueGroupId = (q) => queueRoiGroupMap.value.get(q.queueDefinitionName)?.groupId ?? null
const queueGroupName = (q) => queueRoiGroupMap.value.get(q.queueDefinitionName)?.groupName

const queueHasUngrouped = computed(() => queueStatsRef.value.some((q) => queueGroupId(q) == null))

const { filteredData: filteredQueueStats, groups: queueGroups } = useGroupFilteredData({
  data: queueStatsRef,
  excluded: queueExcluded,
  extractGroupId: queueGroupId,
  extractGroupName: queueGroupName,
  hasUngrouped: queueHasUngrouped
})

const queueGroupFilter = computed(() => ({
  groups: queueGroups.value,
  excluded: queueExcluded.value,
  label: 'Queue groups',
  hasUngrouped: queueHasUngrouped.value
}))

const handleQueueExcluded = (next) => {
  groupFilterStore.setExcluded('operations', 'queue', next)
}

const jobsByState = ref(null)
const averageJobCompletionTime = ref(null)
const jobStatusPerMonth = ref(null)
watch(
  filteredJobStats,
  (jobs) => {
    const opts = emptyStateOptions(jobs, jobStatsRef.value)
    jobsByState.value?.populateComponent(jobs, opts)
    averageJobCompletionTime.value?.populateComponent(jobs, opts)
    jobStatusPerMonth.value?.populateComponent(jobs, opts)
  },
  { flush: 'post' }
)

const taskByStatus = ref(null)
const averageQueueCompletionTime = ref(null)
const tasksPerMonth = ref(null)
watch(
  filteredQueueStats,
  (queues) => {
    const opts = emptyStateOptions(queues, queueStatsRef.value)
    taskByStatus.value?.populateComponent(queues, opts)
    averageQueueCompletionTime.value?.populateComponent(queues, opts)
    tasksPerMonth.value?.populateComponent(queues, opts)
  },
  { flush: 'post' }
)

const overviewCards = ref(null)
watch(
  [filteredJobStats, filteredQueueStats],
  ([jobs, queues]) => {
    const hasRaw = jobStatsRef.value.length > 0 || queueStatsRef.value.length > 0
    const opts = jobs.length === 0 && queues.length === 0 && hasRaw
      ? { emptyMessage: GROUP_FILTER_EMPTY_MESSAGE }
      : {}
    overviewCards.value?.populateComponent(transformOverviewStats(queues, jobs), opts)
  },
  { flush: 'post' }
)

const { updateData, reloadButtonNeedsToSpin } = useRangeGraphUpdater({
  fetchData,
  populateMap
})

const handleRangeChange = (range) => {
  dateRangeStore.setRange('operations', range)
  updateData(range)
}

// Switching the active database re-queries the current range against the new DB.
const handleDatabaseChange = () => {
  updateData(dateRangeStore.getRange('operations'))
}

onMounted(async () => {
  await databaseStore.init()
  updateData(dateRangeStore.getRange('operations'))
})
</script>

<style lang="css">
.box-shadow {
  border: var(--border-card);
  box-shadow: var(--shadow-card);
}

.box-shadow:hover {
  box-shadow: var(--shadow-card-hover);
  transform: translateY(-2px);
}

.display-wrapper {
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  border-radius: var(--border-radius-card);
  margin-right: 20px;
}

main {
  display: grid;
  gap: 16px;
  padding: 16px;
}

.split-row-right {
  grid-template-columns: 2.5fr 1.5fr;
}

.split-row {
  grid-template-columns: 1.5fr 2.5fr;
}

.equal-row {
  grid-template-columns: 1fr 1fr;
}

.row {
  display: grid;
  gap: 5px;
  align-items: stretch;
}

main section {
  padding: 1rem 0.5rem;
}
</style>